/**
 * @copyright 2022 Nuance Communications Inc.
 * All Rights Reserved.
 */
import {
    DetailsListLayoutMode,
    IColumn,
    ICommandBarItemProps,
    SelectionMode
} from "@fluentui/react";
import { ILogger, getLogger } from "@nuance/hux-diagnostics";
import { CmdBuilder, IViewModel } from "@nuance/hap-components";

/**
 * interface for a row in the grid
 */
export interface IGridItem {
    id: string;
    index: number;
    [otherProps: string]: string | number;
}

export interface IGridProps {
    id: string; //unique identifier
    columns: IColumn[];
    items: IGridItem[];
    layout?: DetailsListLayoutMode; //defaults to justified
    selectionMode?: SelectionMode; //defaults to single
    hasFilter?: boolean; //if true displays search/filter input box
    filterColumns?: string[];
    actions?: IGridActionsProps;
    className?: string;
    onSelectRow?: (selectedRows: number[]) => void;
}

export interface IGridState {
    selectedRows: number[];
    filteredItems: IGridItem[];
    searchString?: string;
}

/**
 * Properties for GridActions
 */
export interface IGridActionsProps {
    left: ICommandBarItemProps[];
    right?: ICommandBarItemProps[];
}

const logger: ILogger = getLogger();

export class GridCommands {
    constructor() {
        const cb = new CmdBuilder();
        const cmds = {
            itemsChange: cb.bindCmd(this.itemsChangedHandler),
            onSelectionChanged: cb.bindCmd(this.selectionChangedHandler),
            onFilter: cb.bindCmd(this.filterHandler)
        };

        this.Cmds = cmds;
        this.Reducer = cb.buildReducer<IGridState>(cmds);
    }

    readonly Cmds;
    readonly Reducer;

    /////////////////////////////////////////////////////////////////
    // Command handlers
    /////////////////////////////////////////////////////////////////
    itemsChangedHandler = (
        state: IGridState,
        changeInfo: { items: IGridItem[]; columns?: string[]; id: string }
    ) => {
        logger.trackEvent(`${changeInfo.id} rows changed`);
        const filterInfo = {
            searchString: state.searchString ?? "",
            allItems: changeInfo.items,
            filterColumns: changeInfo.columns
        };
        this.filterHandler(state, filterInfo);
        return state;
    };

    selectionChangedHandler = (state: IGridState, selectedRows: number[]) => {
        // get original index, because the selected rows give is based on sorting and filtering
        state.selectedRows = selectedRows.map(row => state.filteredItems[row]?.index);
        return state;
    };

    filterHandler = (
        state: IGridState,
        filterInfo: {
            searchString: string;
            allItems: IGridItem[];
            filterColumns?: string[];
        }
    ): IGridState => {
        const { searchString, allItems } = filterInfo;
        state.searchString = searchString;
        const text = searchString.toLowerCase();
        if (text) {
            const columns = filterInfo.filterColumns ?? ["id"];
            state.filteredItems = allItems.filter((i: IGridItem) => {
                const match = columns.some(column => {
                    let val = i[column];
                    if (typeof val === "number") val = val.toString();
                    return val.toLowerCase().indexOf(text) > -1;
                });

                return match;
            });
        } else {
            state.filteredItems = allItems;
        }

        return state;
    };
}

type IGridCmds = GridCommands["Cmds"];

export type IGridViewModel = IViewModel<IGridState, IGridCmds>;
